import { useBlacklistAutoRules } from '@hprofits/api-client';
import { SUPPRESS_ERROR_TOAST_META } from '@hprofits/api-client/error-handling';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';

import { apiClient } from '@/api-client';

import { LOAD_ALL_PARAMS } from '../api/blacklistAutoRulesListParams';
import type { RuleGroupRow } from '../types/ruleGroupRow';
import { toRuleGroupRows } from '../utils/toRuleGroupRows';

/**
 * The group a rule belongs to, derived the same way the grouped list derives it.
 *
 * A group is not a stored entity — it is a label over rules sharing an ad-unit-type set, and any
 * type set forms one, even a single rule's. That derivation is why the whole list is needed here
 * and the rule's own `adUnitTypeIds` are not enough. The query key matches the grouped list's and,
 * unlike the list pages, it does not refetch on every mount, so arriving from that list is a cache
 * hit; a new rule has no group, so `/new` loads nothing. A failed load only drops the breadcrumb,
 * hence no error toast.
 */
export const useRuleParentGroup = (ruleId: ComputedRef<number | null>): ComputedRef<RuleGroupRow | undefined> => {
  const query = useBlacklistAutoRules(apiClient, LOAD_ALL_PARAMS, {
    ...SUPPRESS_ERROR_TOAST_META,
    enabled: computed(() => ruleId.value !== null),
  });
  const rows = computed(() => toRuleGroupRows(query.data.value?.items ?? []));
  return computed(() => {
    // Read into a local first: `ruleId.value` does not narrow inside the `find` callback, which
    // leaves an optional `sourceRuleId` compared against a nullable id.
    const id = ruleId.value;
    if (id === null) {
      return undefined;
    }
    const parentId = rows.value.find((row) => row.kind === 'rule' && row.sourceRuleId === id)?.parentId;
    return parentId ? rows.value.find((row) => row.kind === 'group' && row.id === parentId) : undefined;
  });
};
